<template>
  <div
    class="ex-checkpoint-edit-header-root"
    :class="{ 'ex-checkpoint-edit-header-root--no-border': newCheckpointEditAndInspection }"
  >
    <div v-if="showPageChrome" class="ex-page-chrome">
      <div class="ex-chrome-breadcrumb-row">
        <Breadcrumbs
          class="ex-chrome-breadcrumbs"
          :breadcrumbsList="breadcrumbs"
          @onClickBreadcrumb="onBreadcrumbClick"
        />
        <CheckpointCardActions
          class="ex-chrome-card-actions"
          :has_changes="cardHasChanges"
          :status="orgBadgeStatus"
          :checkpoint-data="statusMenuCheckpointData"
          :activate-disabled="activateDisabled"
          @edit="$emit('card-actions-edit')"
          @chatter="$emit('open-chatter')"
          @menu="(payload) => $emit('status-menu-action', payload)"
        />
      </div>
      <CheckpointStatusMenu
        v-if="showStatusMenu"
        :state="statusMenuState"
        :activate-disabled="activateDisabled"
        :proposed-tab-index="proposedTabIndex"
        @activate="$emit('status-menu-activate')"
        @proposeChanges="$emit('status-menu-propose-changes')"
        @publish="$emit('status-menu-publish')"
        @discard="$emit('status-menu-discard')"
        @tab-change="(i) => $emit('status-menu-tab-change', i)"
      />
    </div>
    <div class="ex-checkpoint-edit-header">
      <div class="ex-edit-header-top">
        <CheckpointLevelBanner
          class="ex-edit-header-banner"
          :level="internalLevel"
          @update:level="onLevelUpdate"
        />
        <LanguagePicker
          v-model="selectedLanguage"
          class="ex-edit-header-language"
          :can_translate="canTranslate"
          :available_languages="languagePickerLanguages"
        />
      </div>

      <div class="ex-edit-header-mid">
        <span class="ex-criteria-field-label">Criteria*</span>
        <div class="ex-criteria-row-actions">
          <div
            class="ex-right-buttons"
            :class="{ 'ex-right-buttons--inactive': !hasCriteriaDirty }"
            :inert="!hasCriteriaDirty ? true : undefined"
          >
            <Button
              size="small"
              :outline="'outline-hover'"
              label="Discard"
              class="ex-discard-checkpoint"
              @onClick="onDiscardClick"
            />
            <Button
              size="small"
              :fullwidth="isSaveDisabled ? 'disable' : 'hover'"
              label="Save"
              :class="[
                'ex-save-checkpoint',
                {
                  'ex-save-checkpoint--criteria-blocked':
                    isSaveDisabled && hasCriteriaDirty && isCriteriaEmpty,
                },
              ]"
              :loading="$loader.isAppLoading ? 'left' : null"
              @onClick="onSaveClick"
            />
          </div>
        </div>
      </div>

      <div class="ex-edit-header-criteria checkpoint">
        <ExTextInput
          label=""
          placeholder="Add criteria"
          :value="criteriaInput"
          required
          dropdown-side="left"
          :dropdown-items="dropdownItems"
          :class="[
            'add-criteria',
            {
              /* Lavender criteria side when text is non-empty and highlighted. */
              'input-dirty': highlightCriteriaDirty && !isCriteriaEmpty,
              /* Empty criteria + highlight: #FEF3F2 on text side only (see ::v-deep gradients). */
              'criteria-empty-bg': highlightCriteriaDirty && isCriteriaEmpty,
              'prefix-dirty': isPrefixDirty,
            },
          ]"
          :show-custom-icon="true"
          @dropdown-input="handlePrefixChange"
          :selected-item="criteriaPrefix"
          @input="handleCriteriaInput"
          :edit-modal="true"
        />
      </div>

      <div
        v-if="showVideoProcessing"
        class="ex-edit-header-video-processing"
        role="status"
        aria-live="polite"
      >
        <div class="ex-edit-header-video-processing__loader-wrap" aria-hidden="true">
          <StreamProcessingLoaderSvg class="ex-edit-header-video-processing__loader" />
        </div>
        <p class="ex-edit-header-video-processing__text">Video is being processed ...</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import Button from "@components/buttons/Button.vue";
import Breadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import CheckpointCardActions from "@components/CheckpointCardActions/CheckpointCardActions.vue";
import CheckpointLevelBanner from "@components/CheckpointLevelBanner/CheckpointLevelBanner.vue";
import CheckpointStatusMenu from "@components/CheckpointStatusMenu/CheckpointStatusMenu.vue";
import LanguagePicker from "@components/LanguagePicker/LanguagePicker.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import StreamProcessingLoaderSvg from "@components/StreamProcessingLoaderSvg/StreamProcessingLoaderSvg.vue";
import store from "../../account/src/store";

const props = defineProps({
  level: {
    type: String,
    default: "Apprentice",
    validator: (v) => ["Apprentice", "Professional", "Coach", "Master"].includes(v),
  },
  criteria: {
    type: String,
    required: true,
  },
  prefix: {
    type: String,
    default: undefined,
    validator: (v) => v == null || ["Can", "Does", "Demonstrated", "Proved"].includes(v),
  },
  checkpointPrefix: {
    type: String,
    default: undefined,
    validator: (v) => v == null || ["Can", "Does", "Demonstrated", "Proved"].includes(v),
  },
  language: {
    type: Object,
    default: null,
  },
  languages: {
    type: Array,
    default: () => [],
  },
  availableLanguages: {
    type: Array,
    default: () => [],
  },
  canTranslate: {
    type: Boolean,
    default: false,
  },
  showPageChrome: {
    type: Boolean,
    default: false,
  },
  /** Breadcrumb trail when `showPageChrome` is true. */
  breadcrumbs: {
    type: Array,
    default: () => [
      { text: "Checklist", href: "" },
      { text: "Checkpoint", href: "", disabled: true },
    ],
  },
  cardHasChanges: {
    type: Boolean,
    default: false,
  },
  orgBadgeStatus: {
    type: String,
    default: "active",
  },
  showStatusMenu: {
    type: Boolean,
    default: true,
  },
  statusMenuState: {
    type: String,
    default: "active_propose",
    validator: (value) =>
      [
        "draft_activate_enabled",
        "draft_activate_disabled",
        "active_propose",
        "proposed_viewing_active",
        "proposed_drafting",
        "proposed_publish_disabled",
      ].includes(value),
  },
  proposedTabIndex: {
    type: Number,
    default: null,
  },
  statusMenuCheckpointData: {
    type: Object,
    default: null,
  },
  showVideoProcessing: {
    type: Boolean,
    default: false,
  },
  activateDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:level",
  "update:language",
  "update:criteriaInput",
  "update:criteriaPrefix",
  "update:prefix",
  "discard",
  "save",
  "breadcrumb-click",
  "open-chatter",
  "card-actions-edit",
  "status-menu-activate",
  "status-menu-propose-changes",
  "status-menu-publish",
  "status-menu-discard",
  "status-menu-tab-change",
  "status-menu-action",
]);

const ENGLISH = Object.freeze({
  id: 1,
  name: "English",
  code: "en",
});

const newCheckpointEditAndInspection = computed(() => {
  return store.getters["featureFlags/ideaMDP452"];
});

function normalizeLang(lang) {
  if (lang && typeof lang === "object" && lang.code) {
    return lang;
  }
  return ENGLISH;
}

function langCode(lang) {
  return String(normalizeLang(lang).code).toLowerCase();
}

function resolvePrefixFromProps() {
  const allowed = ["Can", "Does", "Demonstrated", "Proved"];
  const raw = props.prefix ?? props.checkpointPrefix;
  if (raw != null && raw !== "" && allowed.includes(raw)) {
    return raw;
  }
  return "Can";
}

const internalLevel = ref(props.level);
const criteriaInput = ref(props.criteria);
const criteriaPrefix = ref(resolvePrefixFromProps());
const selectedLanguage = ref(normalizeLang(props.language));
const dropdownItems = ref(["Can", "Does", "Demonstrated", "Proved"]);

const initialLevel = ref(props.level);
const initialCriteria = ref(props.criteria);
const initialCriteriaPrefix = ref(resolvePrefixFromProps());
const initialLanguage = ref(normalizeLang(props.language));

const languagePickerLanguages = computed(() => {
  if (props.languages && props.languages.length > 0) {
    return props.languages;
  }
  if (props.availableLanguages && props.availableLanguages.length > 0) {
    return props.availableLanguages;
  }
  return [];
});

watch(
  () => props.level,
  (v) => {
    if (["Apprentice", "Professional", "Coach", "Master"].includes(v)) {
      internalLevel.value = v;
    }
  }
);

watch(
  () => props.criteria,
  (v) => {
    criteriaInput.value = v;
  }
);

watch(
  () => [props.prefix, props.checkpointPrefix],
  () => {
    const next = resolvePrefixFromProps();
    if (next !== criteriaPrefix.value) {
      criteriaPrefix.value = next;
    }
  }
);

watch(
  () => props.language,
  (v) => {
    selectedLanguage.value = normalizeLang(v);
  },
  { deep: true }
);

const isInputDirty = computed(() => criteriaInput.value !== initialCriteria.value);

const isPrefixDirty = computed(() => criteriaPrefix.value !== initialCriteriaPrefix.value);

const isLevelDirty = computed(() => internalLevel.value !== initialLevel.value);

const isLanguageDirty = computed(
  () => langCode(selectedLanguage.value) !== langCode(initialLanguage.value)
);

const isDirty = computed(
  () => isInputDirty.value || isPrefixDirty.value || isLevelDirty.value || isLanguageDirty.value
);

/** Save / Discard only for criteria (text or prefix), not level or language alone. */
const hasCriteriaDirty = computed(() => isInputDirty.value || isPrefixDirty.value);

const isCriteriaEmpty = computed(() => !criteriaInput.value || criteriaInput.value.trim() === "");
const highlightCriteriaDirty = computed(
  () => isInputDirty.value || (isDirty.value && isCriteriaEmpty.value)
);

const isSaveDisabled = computed(() => {
  if (isCriteriaEmpty.value) {
    return true;
  }
  if (!hasCriteriaDirty.value) {
    return true;
  }
  return false;
});

function onLevelUpdate(next) {
  internalLevel.value = next;
  emit("update:level", next);
}

function handleCriteriaInput(value) {
  criteriaInput.value = value;
  emit("update:criteriaInput", value);
}

function normalizePrefix(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.text != null) return String(value.text);
  if (typeof value === "object" && value.value != null) return String(value.value);
  return String(value);
}

function handlePrefixChange(value) {
  const next = normalizePrefix(value) || "Can";
  criteriaPrefix.value = next;
  emit("update:criteriaPrefix", next);
  emit("update:prefix", next);
}

watch(selectedLanguage, (v) => {
  emit("update:language", v);
});

function onDiscardClick() {
  internalLevel.value = initialLevel.value;
  criteriaInput.value = initialCriteria.value;
  criteriaPrefix.value = initialCriteriaPrefix.value;
  selectedLanguage.value = { ...initialLanguage.value };
  emit("update:level", initialLevel.value);
  emit("update:criteriaInput", initialCriteria.value);
  emit("update:criteriaPrefix", initialCriteriaPrefix.value);
  emit("update:prefix", initialCriteriaPrefix.value);
  emit("update:language", { ...initialLanguage.value });
  emit("discard");
}

function commitDirtyBaseline() {
  initialLevel.value = internalLevel.value;
  initialCriteria.value = criteriaInput.value;
  initialCriteriaPrefix.value = criteriaPrefix.value;
  initialLanguage.value = { ...normalizeLang(selectedLanguage.value) };
}

function onSaveClick() {
  if (isSaveDisabled.value) return;
  emit("save", {
    level: internalLevel.value,
    language: { ...normalizeLang(selectedLanguage.value) },
    criteria: criteriaInput.value,
    prefix: criteriaPrefix.value,
  });
  commitDirtyBaseline();
}

function onBreadcrumbClick(breadcrumb) {
  emit("breadcrumb-click", breadcrumb);
}
</script>

<style scoped>
.checkpoint ::v-deep .v-text-field {
  padding-top: 0px !important;
}

.checkpoint
  ::v-deep
  .level-picker.v-text-field
  .v-input__control
  .v-input__slot
  .v-input__append-inner,
.checkpoint
  ::v-deep
  .level-picker.v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  margin-top: 4px;
}
::v-deep .add-criteria .left-can-prepand {
  min-width: 65px;
  top: -10px;
  margin-top: 0 !important;
  padding-top: 0 !important;
}
::v-deep .add-criteria .left-does-prepand {
  min-width: 72px;
  top: -10px;
  margin-top: 0 !important;
  padding-top: 0 !important;
}
::v-deep .add-criteria .left-demonstrated-prepand {
  min-width: 132px;
  top: -10px;
}
::v-deep .add-criteria .left-proved-prepand {
  min-width: 80px;
  top: -10px;
}
::v-deep
  .add-criteria.ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  label.v-label--active {
  margin-left: -60px !important;
}
::v-deep
  .add-criteria.ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  label.v-label--active {
  margin-left: -10px !important;
}

::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-does-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 71px,
    #d9d6fe 71px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 143px,
    #d9d6fe 143px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 91px,
    #d9d6fe 91px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* Empty criteria: reddish fill (#FEF3F2) on main text area only; prefix strip stays white. */
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-does-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #ffffff 0%,
    #ffffff 71px,
    #fef3f2 71px,
    #fef3f2 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #ffffff 0%,
    #ffffff 143px,
    #fef3f2 143px,
    #fef3f2 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #ffffff 0%,
    #ffffff 91px,
    #fef3f2 91px,
    #fef3f2 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 79px,
    white 65px,
    white 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .code-select-wrapper,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-select__selections,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-select__selections {
  background: transparent !important;
}

::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 147px,
    white 137px,
    white 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-select__selections {
  background: transparent !important;
}

::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 95px,
    white 85px,
    white 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-select__selections {
  background: transparent !important;
}

/* Empty criteria + prefix dirty: lavender prefix strip, #FEF3F2 on criteria side only. */
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 79px,
    #fef3f2 79px,
    #fef3f2 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner,
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .code-select-wrapper,
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-select__selections,
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-select__selections {
  background: transparent !important;
}

::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 147px,
    #fef3f2 147px,
    #fef3f2 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-select__selections {
  background: transparent !important;
}

::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 95px,
    #fef3f2 95px,
    #fef3f2 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.criteria-empty-bg.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-select__selections {
  background: transparent !important;
}

::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background-color: #d9d6fe !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty.input-dirty
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .code-select-wrapper {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control fieldset {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-select__selections {
  background: transparent !important;
}

::v-deep .add-criteria.prefix-dirty .v-text-field .v-input__control .v-input__slot input {
  background-color: transparent !important;
}

::v-deep
  .add-criteria.prefix-dirty
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background-color: transparent !important;
}

:deep(.ex-edit-header-language.language-picker) {
  min-width: 160px;
  max-width: 220px;
}

.ex-edit-header-criteria :deep(.ex_text_input > .main_label) {
  display: none;
}

.ex-edit-header-criteria :deep(.ex_text_input) {
  width: 100%;
}

.ex-edit-header-criteria :deep(.ex_text_input .input-search),
.ex-edit-header-criteria :deep(.ex_text_input .v-text-field) {
  width: 100%;
}

.ex-edit-header-criteria :deep(.ex_text_input .v-input) {
  width: 100%;
}
</style>

<style>
.ex-checkpoint-edit-header {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0;
}

.ex-edit-header-top {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  /* Stack above criteria row so level banner dropdown is not covered when criteria is dirty
     (Vuetify layers z-index inside the input; later DOM siblings paint on top by default). */
  position: relative;
  z-index: 5;
}

.ex-edit-header-mid {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin-top: 16px;
  margin-bottom: 8px;
}

.ex-criteria-field-label {
  flex: 0 1 auto;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.ex-criteria-row-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.ex-edit-header-banner {
  flex: 1 1 auto;
  min-width: 0;
}

.ex-edit-header-language {
  flex: 0 0 auto;
  align-self: center;
}

.ex-edit-header-criteria {
  width: 100%;
  min-width: 0;
  margin: 0;
  box-sizing: border-box;
}

.ex-right-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

/* Keep layout size when clean so the criteria row and input do not shift */
.ex-right-buttons--inactive {
  visibility: hidden;
  pointer-events: none;
  user-select: none;
}

.ex-discard-checkpoint {
  width: 80px;
}

.ex-save-checkpoint {
  width: 61px;
}

/* Disabled Save when criteria is empty but form is dirty — light grey, low-contrast label. */
.ex-save-checkpoint--criteria-blocked.storybook-button--btn-disable-small,
.ex-save-checkpoint--criteria-blocked.storybook-button--disable-small {
  background-color: #e4e7ec !important;
  color: #ffffff !important;
  border-color: #e4e7ec !important;
  cursor: not-allowed !important;
}

.ex-checkpoint-edit-header-root {
  display: flex;
  flex-direction: column;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 10px 0px 10px 0px;
}

.ex-checkpoint-edit-header-root--no-border {
  border: none;
}

.ex-page-chrome {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.ex-chrome-breadcrumb-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding: 6px 0 12px;
  box-sizing: border-box;
}

.ex-chrome-breadcrumbs {
  flex: 1 1 auto;
  min-width: 0;
}

.ex-chrome-card-actions {
  flex: 0 0 auto;
}

/* Compact encoding loader: shorter than the inspection full-area loader */
.ex-edit-header-video-processing {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 100px;
  margin-top: 8px;
  padding: 12px 8px 16px;
  box-sizing: border-box;
}

.ex-edit-header-video-processing__loader-wrap {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ex-edit-header-video-processing__loader {
  width: 36px;
  height: 36px;
  display: block;
  flex-shrink: 0;
}

.ex-edit-header-video-processing__text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  text-align: center;
}
</style>
